import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  Container,
  Typography,
  Chip,
  CardContent,
  CardHeader,
  Divider,
  Tooltip,
  Button,
  useTheme,
} from "@mui/material";
import { Security as SecurityIcon, Add as AddIcon } from "@mui/icons-material";
import RiskRegisterTable from "./RiskRegisterTable";
import FilterBar from "../../../../../components/FilterNSearchBar/FilterNSearchBar";
import AddRiskChoiceDialog from "../../components/Dialogs/AddRiskDialogs/AddRiskChoiceDialog";
import AddRiskFromLibraryDialog from "../../components/Dialogs/AddRiskDialogs/AddRiskFromLibraryDialog";
import ManualAddRiskDialog from "../../components/Dialogs/AddRiskDialogs/ManualAddRiskDialog";
import RiskDetailsDialog from "../../components/Dialogs/RiskDetailDialog/RiskDetailsDialog";
import RiskSummary from "../../pages/RiskSummary";
import { styles } from "../../styles/RiskManagement.styles";
import TableSkeleton from "../../../../../components/Skeletons/TableSkeleton";
import { SourceTitle } from "../../../../../utils/enums";
import { TabName } from "../../utils/enums";
import ConfirmSnackbar from "../../../../../components/Snackbar/ConfirmSnackbar";

const RiskRegister = ({
  risksData,
  risksLoading,
  risksRefetch,
  error,
  onStartAssessment,
  hasDraftAssessment = false,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [addRiskStep, setAddRiskStep] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const allRisks = useMemo(() => {
    return risksData?.getRisks?.data || [];
  }, [risksData]);

  useEffect(() => {
    if (allRisks?.length > 0) {
      const uniqueCategories = [
        ...new Set(
          allRisks?.map((risk) => risk?.risk_category_title).filter(Boolean)
        ),
      ];
      setCategories(uniqueCategories);
    }
  }, [allRisks]);

  const filteredRisks = allRisks?.filter((risk) => {
    const matchesSearch =
      !searchQuery ||
      (risk?.title ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (risk?.description ?? "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      (risk?.key ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (risk?.risk_owner_email ?? "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchesCategory =
      !selectedCategory || risk?.risk_category_title === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory(null);
  };

  const handleAddRiskClick = () => setAddRiskStep(SourceTitle.CHOICE);

  const handleAddRiskChoice = (option) => {
    if (option === SourceTitle.LIBRARY) setAddRiskStep(SourceTitle.LIBRARY);
    else if (option === SourceTitle.MANUAL) setAddRiskStep(SourceTitle.MANUAL);
  };

  const handleAddRiskDialogClose = () => setAddRiskStep(null);

  const handleViewDetails = (risk) => {
    if (risk) {
      setSelectedRisk(risk);
      setIsDetailDialogOpen(true);
    }
  };

  const handleDetailDialogClose = () => {
    setIsDetailDialogOpen(false);
    setSelectedRisk(null);
  };

  return (
    <>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
      <RiskSummary risks={filteredRisks} loading={risksLoading} />
      <Card sx={styles.mainCard} variant="outlined">
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.text.strong}
              >
                Risks
              </Typography>
              {(searchQuery || selectedCategory) && (
                <Chip
                  label="Filtered"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: theme.customColors.text.secondary,
                    borderColor: theme.customColors.border.main,
                    background: theme.customColors.background.light,
                  }}
                />
              )}
            </Box>
          }
          sx={styles.cardHeader}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Tooltip title="Add a new risk" arrow>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={handleAddRiskClick}
                    sx={{
                      bgcolor: theme.customColors.button.primary,
                      color: theme.customColors.button.text,
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                      "&:hover": { bgcolor: theme.palette._blue.dark },
                      "&:disabled": {
                        bgcolor: theme.customColors.background.light,
                        color: theme.customColors.text.disabled,
                      },
                    }}
                  >
                    Add New Risk
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  hasDraftAssessment
                    ? "Complete the pending assessment before starting a new assessment"
                    : "Start an assessment to view assessment data"
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    color="primary"
                    sx={{
                      bgcolor: theme.customColors.button.primary,
                      color: theme.customColors.button.text,
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                      "&:hover": { bgcolor: theme.palette._blue.dark },
                      "&:disabled": {
                        bgcolor: theme.customColors.background.light,
                        color: theme.customColors.text.disabled,
                      },
                    }}
                    onClick={onStartAssessment}
                    disabled={hasDraftAssessment}
                  >
                    Start Assessment
                  </Button>
                </span>
              </Tooltip>
            </Box>
          }
        />

        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              pb: 1.5,
              backgroundColor: theme.customColors.background.main,
            }}
          >
            <FilterBar
              searchQuery={searchQuery}
              onSearchChange={(e) => setSearchQuery(e.target.value)}
              filterLabel="Filter by Category"
              filters={categories}
              selectedFilter={selectedCategory}
              onFilterChange={setSelectedCategory}
              onClearFilters={clearFilters}
              countLabel={`${filteredRisks.length} risk${filteredRisks.length !== 1 ? "s" : ""
                } found`}
            />
          </Box>

          <Divider sx={{ borderColor: theme.customColors.border.main }} />

          <Box>
            {error ? (
              <Container
                component="main"
                maxWidth="xs"
                sx={{ textAlign: "center", py: 12 }}
              >
                <Typography variant="h5" color="error" sx={{ mb: 2 }}>
                  Error Loading Risks
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 3 }}
                >
                  {error.message ||
                    "Failed to load risks. Please try again later."}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => window.location.reload()}
                  sx={{ textTransform: "none" }}
                >
                  Retry
                </Button>
              </Container>
            ) : risksLoading ? (
              <TableSkeleton />
            ) : allRisks.length === 0 ? (
              <Box sx={styles.emptyState}>
                <SecurityIcon
                  sx={{
                    fontSize: 48,
                    color: theme.customColors.icons.disabled,
                    mb: 3,
                  }}
                />
                <Typography
                  variant="h5"
                  color="textSecondary"
                  sx={{ mb: 2, fontWeight: 600 }}
                >
                  No risks found
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 4, maxWidth: 400, lineHeight: 1.5 }}
                >
                  No risks are currently registered in your risk register. Start
                  building your risk register by adding risks from our master
                  library or create custom risks manually.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={handleAddRiskClick}
                  sx={styles.addButton}
                  size="large"
                >
                  Add New Risk
                </Button>
              </Box>
            ) : (
              <>
                <RiskRegisterTable
                  risks={filteredRisks}
                  onViewDetails={handleViewDetails}
                  refetch={risksRefetch}
                  setSnackbarInfo={setSnackbarInfo}
                />
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {
        {
          [SourceTitle.CHOICE]: (
            <AddRiskChoiceDialog
              open
              onClose={handleAddRiskDialogClose}
              onSelectOption={handleAddRiskChoice}
            />
          ),
          [SourceTitle.LIBRARY]: (
            <AddRiskFromLibraryDialog
              open
              onClose={handleAddRiskDialogClose}
              onAddRisks={() => {
                risksRefetch();
              }}
              existingRisks={allRisks}
              setSnackbarInfo={setSnackbarInfo}
            />
          ),
          [SourceTitle.MANUAL]: (
            <ManualAddRiskDialog
              open
              onClose={handleAddRiskDialogClose}
              onAddRisks={() => {
                risksRefetch();
              }}
              setSnackbarInfo={setSnackbarInfo}
            />
          ),
        }[addRiskStep]
      }

      {isDetailDialogOpen && (
        <RiskDetailsDialog
          open={isDetailDialogOpen}
          onClose={handleDetailDialogClose}
          risk={selectedRisk}
          refetchItems={risksRefetch}
          type={TabName.REGISTER}
        />
      )}
    </>
  );
};

export default RiskRegister;
